import { apiChannels } from "../../services/api";

// ---------------------------------------------------------------------------
// Events CRUD
// ---------------------------------------------------------------------------

export const listEvents = async (args = {}) => {
  try {
    const params = new URLSearchParams();
    if (args.channelId) params.append("channelId", args.channelId);
    if (args.status) params.append("status", args.status);
    if (args.search) params.append("search", args.search);
    if (args.limit !== undefined) params.append("limit", String(args.limit));
    if (args.cursor) params.append("cursor", args.cursor);
    const qs = params.toString();
    const response = await apiChannels.get(`events${qs ? `?${qs}` : ""}`);
    return response;
  } catch (error) {
    return new Error(`List Events Error: ${error.message}`);
  }
};

export const getEvent = async (eventId) => {
  try {
    const response = await apiChannels.get(
      `events/${encodeURIComponent(eventId)}`
    );
    return response;
  } catch (error) {
    return new Error(`Get Event Error: ${error.message}`);
  }
};

export const createEvent = async (payload) => {
  try {
    const response = await apiChannels.post("events", payload);
    return response;
  } catch (error) {
    return new Error(
      `Create Event Error: ${error.response?.data || error.message}`
    );
  }
};

export const updateEvent = async (eventId, payload) => {
  try {
    const response = await apiChannels.put(
      `events/${encodeURIComponent(eventId)}`,
      payload
    );
    return response;
  } catch (error) {
    return new Error(
      `Update Event Error: ${error.response?.data || error.message}`
    );
  }
};

export const deleteEvent = async (eventId) => {
  try {
    const response = await apiChannels.delete(
      `events/${encodeURIComponent(eventId)}`
    );
    return response;
  } catch (error) {
    return new Error(`Delete Event Error: ${error.message}`);
  }
};

// ---------------------------------------------------------------------------
// Event Content (banner + Puck JSON)
// ---------------------------------------------------------------------------

export const getEventContent = async (eventId) => {
  try {
    const response = await apiChannels.get(
      `events/${encodeURIComponent(eventId)}/content`
    );
    return response;
  } catch (error) {
    if (error.response && error.response.status === 404) {
      return { data: null };
    }
    return new Error(`Get Event Content Error: ${error.message}`);
  }
};

export const upsertEventContent = async (eventId, payload) => {
  try {
    const response = await apiChannels.put(
      `events/${encodeURIComponent(eventId)}/content`,
      payload
    );
    return response;
  } catch (error) {
    return new Error(
      `Save Event Content Error: ${error.response?.data || error.message}`
    );
  }
};

// ---------------------------------------------------------------------------
// Stripe sync (manual retry)
// ---------------------------------------------------------------------------

export const syncEventStripe = async (eventId) => {
  try {
    const response = await apiChannels.post(
      `events/${encodeURIComponent(eventId)}/sync-stripe`
    );
    return response;
  } catch (error) {
    return new Error(
      `Sync Stripe Error: ${error.response?.data || error.message}`
    );
  }
};

// ---------------------------------------------------------------------------
// Registrations / Attendees / Summary
// ---------------------------------------------------------------------------

export const listEventRegistrations = async (eventId, args = {}) => {
  try {
    const params = new URLSearchParams();
    if (args.status) params.append("status", args.status);
    if (args.search) params.append("search", args.search);
    if (args.limit !== undefined) params.append("limit", String(args.limit));
    if (args.cursor) params.append("cursor", args.cursor);
    const qs = params.toString();
    const response = await apiChannels.get(
      `events/${encodeURIComponent(eventId)}/registrations${qs ? `?${qs}` : ""}`
    );
    return response;
  } catch (error) {
    return new Error(`List Registrations Error: ${error.message}`);
  }
};

export const listEventAttendees = async (eventId, args = {}) => {
  try {
    const params = new URLSearchParams();
    if (args.status) params.append("status", args.status);
    if (args.search) params.append("search", args.search);
    if (args.limit !== undefined) params.append("limit", String(args.limit));
    if (args.cursor) params.append("cursor", args.cursor);
    const qs = params.toString();
    const response = await apiChannels.get(
      `events/${encodeURIComponent(eventId)}/attendees${qs ? `?${qs}` : ""}`
    );
    return response;
  } catch (error) {
    return new Error(`List Attendees Error: ${error.message}`);
  }
};

export const getEventSummary = async (eventId) => {
  try {
    const response = await apiChannels.get(
      `events/${encodeURIComponent(eventId)}/summary`
    );
    return response;
  } catch (error) {
    return new Error(`Event Summary Error: ${error.message}`);
  }
};

/**
 * Re-syncs a single registration's payment status with Stripe.
 * Used by the per-row "Verificar pagamento" button on the registrations
 * page. On a pending → paid transition the backend will also flip attendees
 * to confirmed and send the Mailjet confirmation emails — exactly what the
 * Stripe webhook does when it fires normally. This endpoint is the manual
 * fallback for when the webhook didn't reach us.
 */
export const checkEventRegistrationStatus = async (registrationId) => {
  try {
    const response = await apiChannels.post(
      `events/registrations/${encodeURIComponent(registrationId)}/check-status`
    );
    return response;
  } catch (error) {
    return new Error(
      `Check Registration Status Error: ${error.response?.data?.error || error.response?.data || error.message}`
    );
  }
};

export const updateEventAttendee = async (attendeeId, payload) => {
  try {
    const response = await apiChannels.patch(
      `events/attendees/${encodeURIComponent(attendeeId)}`,
      payload
    );
    return response;
  } catch (error) {
    return new Error(
      `Update Attendee Error: ${error.response?.data || error.message}`
    );
  }
};

// ---------------------------------------------------------------------------
// Event Coupons (per-event discount codes)
// ---------------------------------------------------------------------------

export const listEventCoupons = async (eventId) => {
  try {
    const response = await apiChannels.get(
      `events/${encodeURIComponent(eventId)}/coupons`
    );
    return response;
  } catch (error) {
    return new Error(`List Coupons Error: ${error.message}`);
  }
};

export const createEventCoupon = async (eventId, payload) => {
  try {
    const response = await apiChannels.post(
      `events/${encodeURIComponent(eventId)}/coupons`,
      payload
    );
    return response;
  } catch (error) {
    return new Error(
      `Create Coupon Error: ${error.response?.data || error.message}`
    );
  }
};

export const updateEventCoupon = async (eventId, couponId, payload) => {
  try {
    const response = await apiChannels.put(
      `events/${encodeURIComponent(eventId)}/coupons/${encodeURIComponent(
        couponId
      )}`,
      payload
    );
    return response;
  } catch (error) {
    return new Error(
      `Update Coupon Error: ${error.response?.data || error.message}`
    );
  }
};

export const deleteEventCoupon = async (eventId, couponId) => {
  try {
    const response = await apiChannels.delete(
      `events/${encodeURIComponent(eventId)}/coupons/${encodeURIComponent(
        couponId
      )}`
    );
    return response;
  } catch (error) {
    return new Error(`Delete Coupon Error: ${error.message}`);
  }
};

/**
 * Downloads attendees CSV directly from browser (triggers file save).
 * Uses the same authenticated axios instance so the Bearer token travels
 * with the request.
 */
export const exportEventAttendeesCsv = async (eventId, eventSlug) => {
  try {
    const response = await apiChannels.get(
      `events/${encodeURIComponent(eventId)}/attendees/export`,
      { responseType: "blob" }
    );
    const blob = new Blob([response.data], { type: "text/csv;charset=utf-8" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `attendees_${eventSlug || eventId}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    return { data: { ok: true } };
  } catch (error) {
    return new Error(`Export Attendees Error: ${error.message}`);
  }
};

/** Public URL of an event on the Next.js site (opens in a new tab from the admin). */
export function publicEventUrl(slug) {
  const base = (
    process.env.REACT_APP_SITE_URL || "https://ministeriospaodiario.com.br"
  ).replace(/\/+$/, "");
  return `${base}/eventos/${encodeURIComponent(slug)}`;
}
